/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { Spacer, theme } from "@nordcloud/gnui";
import {
  BrickLoader,
  Pagination,
  UniversalWrap,
  CostTableData,
  StackBarChart,
  getCostAnalysisTableColumns,
  generateLinkParameters,
  mapStackBarChartColors,
  ChartType,
  CostAnalysisFields,
  Granularity,
  LinkQueryParameterName,
  Period,
} from "~/components";
import { useCurrency } from "~/hooks";
import { isNotEmpty } from "~/tools";
import { useApplicationsCostPerCategory } from "../hooks/useApplicationsCostPerCategory";

type Props = {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  showCostTable: boolean;
  showOtherCategoriesCost: boolean;
  handleOtherCostsSwitch: (enable: boolean) => void;
};

export function ApplicationsCostPerCategoryView({
  startDate,
  endDate,
  granularity,
  period,
  showCostTable,
  showOtherCategoriesCost,
  handleOtherCostsSwitch,
}: Props) {
  const { currency } = useCurrency();

  const {
    costPerCategoryChartData,
    costPerCategoryTableData,
    costPerCategoryTotal,
    loading,
    error,
    count,
    categories,
    isCorrections,
  } = useApplicationsCostPerCategory({
    startDate,
    endDate,
    granularity,
    showOtherCategoriesCost,
    handleOtherCostsSwitch,
  });

  const stackBarColors = useMemo(
    () => mapStackBarChartColors(categories),
    [categories]
  );

  const allColumns = useMemo(
    () =>
      getCostAnalysisTableColumns({
        startDate,
        endDate,
        currency,
        data: costPerCategoryTableData,
        period,
        chartType: ChartType.COST_PER_CATEGORY,
        colors: stackBarColors,
        linkParameters: generateLinkParameters(
          LinkQueryParameterName.APPLICATION,
          []
        ),
      }),
    [
      startDate,
      endDate,
      currency,
      costPerCategoryTableData,
      period,
      showOtherCategoriesCost,
      stackBarColors,
    ]
  );

  // hide total column when period is month
  const columns = useMemo(
    () =>
      period === Period.MONTH
        ? allColumns.filter((column) => column.header !== "Total")
        : allColumns,
    [allColumns, period]
  );

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <ParentSize>
        {(parent) => (
          <StackBarChart
            width={parent.width}
            height={280}
            data={costPerCategoryChartData}
            granularity={granularity}
            loading={loading}
            startDate={startDate}
            endDate={endDate}
            period={period}
            colors={stackBarColors}
            showOthers={showOtherCategoriesCost}
            chartType={ChartType.COST_PER_CATEGORY}
            keys={
              isCorrections
                ? [...categories, CostAnalysisFields.CORRECTION_COST]
                : categories
            }
          />
        )}
      </ParentSize>
      <CostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={costPerCategoryTableData}
        chartType={ChartType.COST_PER_CATEGORY}
        total={costPerCategoryTotal}
        tableError={error}
        columns={columns}
        isLoading={loading}
        showCostTable={showCostTable && isNotEmpty(costPerCategoryChartData)}
        hasFooter={false}
        hideHeader={period === Period.MONTH}
      />
      <Spacer height={theme.spacing.spacing03} />
      <Pagination small count={count ?? 0} />
    </UniversalWrap>
  );
}
